import type { ReactElement } from 'react';
import { useSeason, type Season } from '@/lib/season';
import { cn } from '@/lib/utils';
import { LEAF_MAPLE, LEAF_MAPLE_STEM, LEAF_OVAL, LEAF_OVAL_VEIN, PUMPKIN, PUMPKIN_LOBES, PUMPKIN_STEM } from './shapes';

/**
 * Мелкие сезонные детали интерфейса (23.10.2026, по просьбе Глеба «мало деталей»):
 * значок у заголовков, шапка на аватаре в профиле, украшения плиток и групп, украшение
 * главной кнопки. Всё статичное и под текстом: декор не должен мешать читать. Нет
 * сезона: компоненты ничего не рисуют. Класс `tl-season` даёт «Схеме» линейный вид.
 */

/* ---------- Значок у заголовка ---------- */

export function SeasonGlyph({ className }: { className?: string }) {
  const season = useSeason();
  if (!season) return null;
  return (
    <svg aria-hidden viewBox="0 0 24 24" className={cn('tl-season inline-block size-[0.9em] shrink-0', className)}>
      {GLYPHS[season]}
    </svg>
  );
}

const GLYPHS: Record<Season, ReactElement> = {
  autumn: (
    <>
      <path d={LEAF_MAPLE} fill="#e0912f" data-fill />
      <path d={LEAF_MAPLE_STEM} stroke="#a8401f" strokeWidth="1.2" strokeLinecap="round" />
    </>
  ),
  halloween: (
    <>
      <path d={PUMPKIN} fill="#f28a1c" data-fill />
      <path d={PUMPKIN_LOBES} stroke="rgba(150,60,0,0.5)" strokeWidth="0.9" fill="none" />
      <path d={PUMPKIN_STEM} stroke="#4f7d2a" strokeWidth="1.8" strokeLinecap="round" fill="none" />
      {/* Лицо фонаря: глаза треугольниками и зубастый рот. */}
      <path d="M7.6 11.6l1.9-2.4 1.6 2.4zM12.9 11.6l1.6-2.4 1.9 2.4zM7.2 14.2c1.4 2.4 8.2 2.4 9.6 0l-1.4.4-.8 1.1-1-1-1.6.9-1.6-.9-1 1-.8-1.1z" fill="#3a1600" />
    </>
  ),
  winter: (
    <g stroke="#8ecdf5" strokeWidth="1.6" strokeLinecap="round" fill="none">
      <path d="M12 2v20M3.3 7l17.4 10M3.3 17L20.7 7" />
      <path d="M9.5 3.8L12 6l2.5-2.2M9.5 20.2L12 18l2.5 2.2M4 10.2l3.2-.9-.8-3.1M20 13.8l-3.2.9.8 3.1M4 13.8l3.2.9-.8 3.1M20 10.2l-3.2-.9.8-3.1" />
    </g>
  ),
  newyear: (
    <>
      <path d="M12 3.5l6 8h-3l4.5 6h-15l4.5-6h-3z" fill="#2f9e5b" data-fill />
      <path d="M11 17.5h2V21h-2z" fill="#8a5a2b" />
      <path d="M12 1l.9 1.8 2 .3-1.4 1.4.3 2L12 5.6l-1.8.9.3-2-1.4-1.4 2-.3z" fill="#ffd05a" />
      <circle cx="10" cy="11.5" r="1" fill="#ff5a5f" />
      <circle cx="14.2" cy="14.5" r="1" fill="#5ad1ff" />
      <circle cx="9.2" cy="15.6" r="1" fill="#ffd05a" />
    </>
  ),
};

/* ---------- Шапка на аватаре ---------- */

/** Ложится поверх круглого аватара 84px: родитель `relative`. */
export function SeasonAvatarHat() {
  const season = useSeason();
  if (!season) return null;
  if (season === 'autumn') {
    return (
      <span aria-hidden className="tl-season pointer-events-none absolute -right-3 -top-2 block size-12">
        <svg viewBox="0 0 24 24" className="absolute right-1 top-0 size-8 rotate-[24deg] drop-shadow-md">
          <path d={LEAF_MAPLE} fill="#d4572a" data-fill />
          <path d={LEAF_MAPLE_STEM} stroke="#7a2c12" strokeWidth="1" fill="none" />
        </svg>
        <svg viewBox="0 0 24 24" className="absolute bottom-0 left-0 size-6 -rotate-[30deg] drop-shadow-md">
          <path d={LEAF_OVAL} fill="#e9b23c" data-fill />
          <path d={LEAF_OVAL_VEIN} stroke="rgba(80,30,10,0.45)" strokeWidth="0.8" fill="none" />
        </svg>
      </span>
    );
  }
  return (
    <svg
      aria-hidden
      viewBox="0 0 64 48"
      className="tl-season pointer-events-none absolute -top-[34px] left-1/2 h-[54px] w-[72px] -translate-x-[54%] -rotate-[14deg] drop-shadow-lg"
    >
      {HATS[season]}
    </svg>
  );
}

const HATS: Record<Exclude<Season, 'autumn'>, ReactElement> = {
  halloween: (
    <>
      {/* Ведьмина шляпа: поля, конус с заломом, оранжевая лента и пряжка. */}
      <ellipse cx="32" cy="41" rx="29" ry="6" fill="#2b2140" stroke="rgba(255,255,255,0.18)" data-fill />
      <path d="M17 39C22 28 27 15 35 6c2.5-2.8 6-3.4 9-2-3.5 1.6-5 4.5-4.6 8.2L47 39z" fill="#2b2140" stroke="rgba(255,255,255,0.18)" data-fill />
      <path d="M18.4 34.5C27 37 38 37 45.7 34.5L47 39c-9 2.6-21 2.6-30 0z" fill="#ff8a1f" />
      <rect x="28.5" y="34.6" width="7" height="5.4" rx="1" fill="none" stroke="#ffd05a" strokeWidth="1.6" />
    </>
  ),
  newyear: (
    <>
      {/* Колпак Деда Мороза: красный конус, мех по краю и помпон. */}
      <path d="M10 36C14 18 26 6 42 7c9 .6 14 6 15 14l-6 2c-2-5-5-7.5-9-7 6 6 8 13 8 20z" fill="#e23b3b" data-fill />
      <rect x="6" y="32" width="50" height="11" rx="5.5" fill="#fbfbfb" data-fill />
      <circle cx="56" cy="23" r="6.5" fill="#fbfbfb" data-fill />
    </>
  ),
  winter: (
    <>
      {/* Вязаная шапка: купол в полоску, отворот в рубчик, помпон. */}
      <path d="M11 37c0-17 9-26 21-26s21 9 21 26z" fill="#3b82f6" data-fill />
      <path d="M13.5 26h37M16.5 19h31" stroke="#e8f1ff" strokeWidth="3" />
      <rect x="8" y="33" width="48" height="11" rx="4.5" fill="#2563eb" data-fill />
      <path d="M15 35v7M21 35v7M27 35v7M33 35v7M39 35v7M45 35v7M51 35v7" stroke="rgba(255,255,255,0.35)" strokeWidth="1.4" />
      <circle cx="32" cy="9" r="6.5" fill="#f4f8ff" data-fill />
    </>
  ),
};

/* ---------- Украшение плитки и группы ---------- */

/** Лежит в правом верхнем углу плитки или группы. Родитель `relative overflow-hidden`.
 *  `compact` для группы строк: справа у первой строки стрелка, крупная паутина её закрывала.
 *  `!border-0`: группа рисует разделители `divide-y` между детьми, и без него под
 *  украшением появлялась лишняя черта. */
export function SeasonCorner({ compact }: { compact?: boolean }) {
  const season = useSeason();
  if (!season) return null;
  if (season === 'winter' || season === 'newyear') {
    return (
      <span aria-hidden className="tl-season pointer-events-none absolute inset-x-0 top-0 z-[1] block h-[9px] !border-0" data-kind="frost">
        <svg className="absolute inset-0 size-full" viewBox="0 0 200 9" preserveAspectRatio="none">
          <path
            d="M0 0H200V3.5C193 5 188 3 181 4.5 175 6 172 8.5 168 8.5 164.5 8.5 163 5.5 158 5 148 4 140 6 131 5 120 3.8 113 5.5 104 5 96 4.6 92 3 84 4 77 5 74 8 70 8 66.5 8 65 5 59 4.5 49 3.7 41 5.5 31 4.7 22 4 15 3 8 4.5 4 5.3 1.5 4.8 0 4z"
            fill="var(--season-snow)"
            data-fill
          />
        </svg>
        {season === 'newyear' && !compact && (
          <span className="absolute right-6 top-0 block">
            <span className="absolute left-1/2 top-0 block h-[18px] w-px -translate-x-1/2 bg-[var(--t-muted)] opacity-60" />
            <svg viewBox="0 0 24 24" className="absolute left-1/2 top-[15px] size-[18px] -translate-x-1/2">
              <rect x="9.5" y="1" width="5" height="4" rx="1" fill="#c9a24a" />
              <circle cx="12" cy="14" r="9" fill="#e23b3b" data-fill />
              <path d="M4.5 12.5c4 2 11 2 15 0" stroke="#ffd05a" strokeWidth="1.4" fill="none" />
              <circle cx="8.6" cy="10" r="2" fill="rgba(255,255,255,0.55)" />
            </svg>
          </span>
        )}
      </span>
    );
  }
  if (season === 'halloween') {
    const rays = [0, 22, 45, 68, 90].map((d) => (d * Math.PI) / 180);
    const at = (r: number, a: number) => `${100 - Math.sin(a) * r},${Math.cos(a) * r}`;
    return (
      <svg
        aria-hidden
        viewBox="0 0 100 100"
        className={cn(
          'tl-season pointer-events-none absolute right-0 top-0 !border-0 text-[var(--t-muted)] opacity-50',
          compact ? 'size-[30px]' : 'size-[54px]',
        )}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      >
        {rays.map((a, i) => (
          <path key={i} d={`M100,0 L${at(96, a)}`} />
        ))}
        {[30, 58, 86].map((r) => (
          <path
            key={r}
            d={rays
              .slice(0, -1)
              .map((a, i) => `${i === 0 ? `M${at(r, a)} ` : ''}Q${at(r * 0.84, (a + rays[i + 1]) / 2)} ${at(r, rays[i + 1])}`)
              .join(' ')}
          />
        ))}
      </svg>
    );
  }
  return (
    <span
      aria-hidden
      className={cn('tl-season pointer-events-none absolute -right-2 -top-2 block !border-0', compact ? 'size-8 scale-75 origin-top-right' : 'size-11')}
      data-kind="leaves"
    >
      <svg viewBox="0 0 24 24" className="absolute right-1 top-1 size-7 rotate-[200deg] opacity-90">
        <path d={LEAF_MAPLE} fill="#d4572a" data-fill />
      </svg>
      <svg viewBox="0 0 24 24" className="absolute bottom-0 right-6 size-4 rotate-[120deg] opacity-80">
        <path d={LEAF_OVAL} fill="#e9b23c" data-fill />
      </svg>
    </span>
  );
}

/* ---------- Украшение главной кнопки ---------- */

/** Внутри круга главного действия: родитель `relative`. Выходит за край круга намеренно.
 *  Размер стилем, а не классом: темы задают размер всем `svg` в круге своими правилами. */
export function SeasonButtonAccent({ at }: { at: 'icon' | 'button' }) {
  const season = useSeason();
  if (!season) return null;
  // Круг главного действия есть только в «Графите». В остальных темах кнопка это
  // плашка, а значок в ней мелкий, и украшение закрывало бы его: там оно на углу кнопки.
  const where = at === 'icon' ? 'tl-btn-season-ic' : 'tl-btn-season-btn';
  if (season === 'halloween') {
    return (
      <svg aria-hidden viewBox="0 0 24 24" className={`tl-season tl-btn-season ${where} pointer-events-none absolute -bottom-1.5 -right-2 drop-shadow-md`} style={{ width: 26, height: 26 }}>
        {GLYPHS.halloween}
      </svg>
    );
  }
  if (season === 'newyear') {
    // Веточка остролиста с ягодами на верхнем краю.
    return (
      <svg aria-hidden viewBox="0 0 32 20" className={`tl-season tl-btn-season ${where} pointer-events-none absolute -right-2 -top-2 drop-shadow-md`} style={{ width: 32, height: 20 }}>
        <path d="M15 11C10 4 4 4 1 7c3 0 4 2 3.5 4 2-1 4 0 4.5 2 1.5-2 4-2.5 6-2z" fill="#2f9e5b" data-fill />
        <path d="M17 11c5-7 11-7 14-4-3 0-4 2-3.5 4-2-1-4 0-4.5 2-1.5-2-4-2.5-6-2z" fill="#2f9e5b" data-fill />
        <circle cx="14" cy="13" r="2.6" fill="#e23b3b" />
        <circle cx="18.2" cy="13.4" r="2.6" fill="#e23b3b" />
        <circle cx="16.1" cy="10.4" r="2.6" fill="#ff5a5f" />
      </svg>
    );
  }
  if (season === 'winter') {
    return (
      <svg aria-hidden viewBox="0 0 58 12" className={`tl-season tl-btn-season ${where} pointer-events-none absolute inset-x-[6px] -top-[3px] drop-shadow-sm`} style={{ width: 'calc(100% - 12px)', height: 12 }} preserveAspectRatio="none">
        <path d="M3 9C10 2 48 2 55 9c-3 1-5-.5-7 .3-2 .8-2.5 3-4.5 3-1.8 0-2.3-2.3-4.5-2.6-3-.4-5 .8-8 .5-3-.3-4.4-1.6-7-1.2-2.7.4-3 2.6-5 2.6-1.8 0-2.3-2-4.3-2.6C6.7 8.6 5 9.8 3 9z" fill="var(--season-snow)" data-fill />
      </svg>
    );
  }
  return (
    <svg aria-hidden viewBox="0 0 24 24" className={`tl-season tl-btn-season ${where} pointer-events-none absolute -right-2 -top-2 rotate-[30deg] drop-shadow-md`} style={{ width: 22, height: 22 }}>
      <path d={LEAF_MAPLE} fill="#e0912f" data-fill />
    </svg>
  );
}
